import React, { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { ShieldCheck, Smartphone, Fingerprint, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";

/**
 * Self-service MFA enrollment — authenticator app (TOTP) and Passkey only.
 * SMS is intentionally not offered (less secure). Surfaced under
 * Settings > Account for every logged-in user; admins can additionally
 * force MFA for specific users from Users & scopes, or org-wide from the
 * Admin tab (see MfaAdminSettings.jsx).
 */
export default function MfaSettings() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [removePasskeyId, setRemovePasskeyId] = useState(null);
  const [deviceName, setDeviceName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/mfa/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startTotpSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/totp/setup");
      setSetupData(data);
      setSetupOpen(true);
    } catch (e) {
      toast({ title: "Couldn't start setup", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const confirmTotpSetup = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/totp/confirm", { code: confirmCode });
      toast({ title: "Authenticator app enabled" });
      setSetupOpen(false);
      setConfirmCode("");
      load();
    } catch (e) {
      toast({ title: "Incorrect code", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setBusy(false); }
  };

  // Shared password-confirm dialog handles BOTH "disable TOTP" and "remove
  // passkey" — removing a security factor should always need re-auth, same
  // as disabling one, so a stolen session token alone can't strip MFA.
  const disableTotp = async () => {
    setBusy(true);
    try {
      if (removePasskeyId) {
        await api.delete(`/auth/mfa/passkey/${removePasskeyId}`, { data: { password: disablePw } });
        toast({ title: "Passkey removed" });
      } else {
        await api.post("/auth/mfa/totp/disable", { password: disablePw });
        toast({ title: "Authenticator app disabled" });
      }
      setDisableOpen(false);
      setDisablePw("");
      setRemovePasskeyId(null);
      load();
    } catch (e) {
      toast({ title: "Couldn't confirm", description: e?.response?.data?.detail || "Incorrect password", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const addPasskey = async () => {
    setBusy(true);
    try {
      const { data: opts } = await api.post("/auth/mfa/passkey/register-options");
      const credential = await startRegistration({ optionsJSON: JSON.parse(opts.options) });
      await api.post("/auth/mfa/passkey/register-verify", { credential, device_name: deviceName || "Passkey" });
      toast({ title: "Passkey added" });
      setDeviceName("");
      load();
    } catch (e) {
      toast({ title: "Couldn't add passkey", description: e?.response?.data?.detail || "Your browser/device may not support this.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const removePasskey = (id) => {
    setRemovePasskeyId(id);
    setDisableOpen(true);
  };

  if (!status) return <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="mfa-settings-section">
      {(status.forced || status.required_globally) && !status.totp_enabled && status.passkeys.length === 0 && (
        <div className={`rounded-lg border p-3 text-[12.5px] flex items-start gap-2 ${status.setup_blocking ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-amber-500/40 bg-amber-500/5 text-amber-600 dark:text-amber-400"}`} data-testid="mfa-required-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Your admin requires MFA on this account.{" "}
            {status.setup_blocking ? "Please set up a method now to continue using UC Vigil." :
              `Please set up a method by ${new Date(status.setup_deadline).toLocaleDateString()}.`}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between rounded-lg border border-border p-3">
        <div className="flex items-center gap-3">
          <Smartphone className="w-5 h-5 text-muted-foreground" />
          <div>
            <div className="text-[13px] font-medium flex items-center gap-2">
              Authenticator app
              {status.totp_enabled && <Badge variant="secondary" data-testid="totp-enabled-badge">Enabled</Badge>}
            </div>
            <div className="text-[12px] text-muted-foreground">Google Authenticator, Authy, 1Password, etc.</div>
          </div>
        </div>
        {status.totp_enabled ? (
          <Button variant="outline" size="sm" onClick={() => { setRemovePasskeyId(null); setDisableOpen(true); }} data-testid="totp-disable-button">Disable</Button>
        ) : (
          <Button size="sm" onClick={startTotpSetup} disabled={busy} data-testid="totp-setup-button">Set up</Button>
        )}
      </div>

      <div className="rounded-lg border border-border p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Fingerprint className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="text-[13px] font-medium">Passkeys</div>
              <div className="text-[12px] text-muted-foreground">Face ID, Touch ID, Windows Hello, or a security key.</div>
            </div>
          </div>
        </div>
        {status.passkeys.length > 0 && (
          <div className="space-y-1.5 pl-8">
            {status.passkeys.map((pk) => (
              <div key={pk.id} className="flex items-center justify-between text-[12.5px]" data-testid={`passkey-row-${pk.id}`}>
                <span>{pk.device_name}</span>
                <button onClick={() => removePasskey(pk.id)} className="text-muted-foreground hover:text-destructive" data-testid={`passkey-remove-${pk.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="pl-8 flex gap-2 pt-1">
          <Input placeholder="Device name (e.g. MacBook Touch ID)" value={deviceName} onChange={(e) => setDeviceName(e.target.value)}
            className="h-8 text-[12.5px]" data-testid="passkey-device-name-input" />
          <Button size="sm" onClick={addPasskey} disabled={busy} data-testid="passkey-add-button">Add passkey</Button>
        </div>
      </div>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-sm" data-testid="totp-setup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Set up authenticator app</DialogTitle>
            <DialogDescription>Scan the QR code with your authenticator app, then enter the 6-digit code it shows.</DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-3">
              <img src={setupData.qr_code_data_uri} alt="TOTP QR code" className="mx-auto w-44 h-44" data-testid="totp-qr-code" />
              <div className="text-[11px] text-muted-foreground text-center">Can't scan? Enter manually: <span className="font-mono">{setupData.secret}</span></div>
              <div className="space-y-1.5">
                <Label>6-digit code</Label>
                <Input value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                  className="text-center font-mono tracking-[0.4em]" data-testid="totp-confirm-code-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupOpen(false)}>Cancel</Button>
            <Button onClick={confirmTotpSetup} disabled={busy || confirmCode.length !== 6} data-testid="totp-confirm-button">Confirm & enable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disableOpen} onOpenChange={(open) => { setDisableOpen(open); if (!open) setRemovePasskeyId(null); }}>
        <DialogContent className="max-w-sm" data-testid="totp-disable-dialog">
          <DialogHeader>
            <DialogTitle>{removePasskeyId ? "Remove passkey" : "Disable authenticator app"}</DialogTitle>
            <DialogDescription>Enter your password to confirm.</DialogDescription>
          </DialogHeader>
          <Input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} data-testid="totp-disable-password-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={disableTotp} disabled={busy || !disablePw} data-testid="totp-disable-confirm-button">{removePasskeyId ? "Remove" : "Disable"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
